import { debounce } from 'lib-utils';

class NovaFiltersWrapper extends HTMLElement {
  constructor() {
    super();

    // Cache for fetched HTML responses
    this.filterData = [];

    // Bind instance methods
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.closeDetails = this.closeDetails.bind(this);
    this.onHistoryChange = this.onHistoryChange.bind(this);
    this.onSubmitHandler = this.onSubmitHandler.bind(this);
    this.closeMobileFilters = this.closeMobileFilters.bind(this);

    // Debounced submit (500ms)
    this.debouncedOnSubmit = debounce(this.onSubmitHandler, 500).bind(this);
  }

  connectedCallback() {
    // 1) Bind your facet-remove buttons
    this.bindActiveFacetButtonEvents();

    // 2) Watch back/forward
    window.addEventListener('popstate', this.onHistoryChange);

    // 3) Close any <details> if you click outside
    document.body.addEventListener('click', this.closeDetails);

    // 4) Live-update sorting inputs
    this.querySelectorAll('.js-sort-input').forEach(input => {
      input.addEventListener('sl-change', this.debouncedOnSubmit);
    });

    // 5) Live-update filter form inputs
    const facetForm = this.querySelector('form');
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit);

    // 6) Initialize your slide-out open/close logic
    this.initSlideoutTriggers();
  }

  /**
   * Wire up filter-button, overlay, close-button, and breakpoint close.
   */
  initSlideoutTriggers() {
    const body = document.body;
    const filterButton = document.querySelector('.slideout-filters__filter-button');
    const overlay = document.querySelector('.filter-overlay');
    const closeButton = document.querySelector('.js-close');

    const closeFilters = () => body.classList.remove('filter-open');

    if (window.innerWidth < 990) {
      const details = this.querySelectorAll('.details-dropdown')
      details.forEach((detail, index) => {
        if (index > 0) {
          detail.removeAttribute('open')
        }
      })
    }

    if (filterButton) {
      filterButton.classList.remove('loading');
      filterButton.addEventListener('click', event => {
        event.preventDefault();
        // Toggle only on mobile
        if (window.innerWidth < 990) {
          body.classList.toggle('filter-open');
        }
      });
    }
    filterButton.addEventListener('click', event => {
      console.log('filter button clicked, before toggle—width:', window.innerWidth);
      event.preventDefault();
      if (window.innerWidth < 990) {
        document.body.classList.toggle('filter-open');
        console.log('toggled filter-open, now:', document.body.classList.contains('filter-open'));
      }
    });


    if (overlay) overlay.addEventListener('click', closeFilters);
    if (closeButton) closeButton.addEventListener('click', closeFilters);
    if (overlay) overlay.addEventListener('click', closeFilters);

    // Auto-close if you resize into desktop
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 990) body.classList.remove('filter-open');
    });
  }

  closeMobileFilters() {
    this.dispatchEvent(new CustomEvent('nova:modal:close', { bubbles: true }));
  }

  closeDetails(event) {
    this.querySelectorAll('.js-filter').forEach(el => {
      if (!el.contains(event.target)) el.open = false;
    });
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || '';
    this.renderPage(searchParams, null, /*updateURLHash=*/false);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const urlParams = new URL(event.currentTarget.href).searchParams.toString();
    this.toggleActiveFacets();
    this.renderPage(urlParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    const data = new FormData(form);
    const clean = new URLSearchParams();

    for (let [k, v] of data.entries()) if (v !== '') clean.append(k, v);
    this.renderPage(clean.toString(), event);
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach(el =>
      el.classList.toggle('disabled', disable)
    );
  }

  renderPage(searchParams, event, updateURLHash = true) {
    // 1) Open or close the slide-out based on whether there's an active filter
    // const shouldOpen = Boolean(searchParams) && window.innerWidth < 990;
    // document.body.classList.toggle('filter-open', shouldOpen);

    // 2) Show loading states
    // document
    //   .querySelector('.slideout-filters__inner')
    //   ?.classList.add('loading');
    document
      .querySelector('.mobile-filter__submit')
      ?.classList.add('loading');
    const grid = document
      .getElementById('main-collection-product-grid')
      ?.querySelector('.js-product-grid');
    if (grid) grid.classList.add('loading', 'filtered');

    // 3) Build URL
    const sections = this.getSections();
    const sectionQuery = sections.map(s => `sections[]=${s.section}`).join('&');
    const url = `${window.location.pathname}?${sectionQuery}&${searchParams}`;

    // 4) Cache hit?
    const cached = this.filterData.find(item => item.url === url);
    if (cached) {
      this.updateSections(cached.html, event);
      if (updateURLHash) this.updateURLHash(searchParams);
      return;
    }

    // 5) Fetch fresh
    fetch(url)
      .then(r => r.json())
      .then(json => {
        const gridKey = document.getElementById('main-collection-product-grid').dataset.id;
        const filterKey = document.getElementById('main-collection-filters').dataset.id;
        const bannerEl = document.getElementById('main-collection-banner');
        const bannerKey = bannerEl?.dataset.id ?? null;

        const gridHTML = json[gridKey];
        const filtersHTML = json[filterKey];
        const bannerHTML = bannerKey ? json[bannerKey] : '';

        // Cache & swap
        this.filterData.push({
          url,
          html: { gridHTML, filtersHTML, bannerHTML }
        });
        this.updateSections({ gridHTML, filtersHTML, bannerHTML }, event);

        if (updateURLHash) this.updateURLHash(searchParams);
      })
      .catch(err => {
        console.error('Error fetching filter results:', err);
        this.reloadPage(searchParams);
      });
  }

  updateSections(htmlSections, event) {
    try {
      const parser = new DOMParser();

      // — Update product grid
      const gridDoc = parser.parseFromString(htmlSections.gridHTML, 'text/html');
      const newGrid = gridDoc.querySelector('#main-collection-product-grid .js-product-grid');
      const currentGrid = document.querySelector('#main-collection-product-grid .js-product-grid');
      if (newGrid && currentGrid) {
        currentGrid.innerHTML = newGrid.innerHTML;
        currentGrid.classList.remove('loading');
      }

      // — Update filters container & form
      const filtersDoc = parser.parseFromString(htmlSections.filtersHTML, 'text/html');
      const newFilters = filtersDoc.getElementById('main-collection-filters');
      const currentFilters = document.getElementById('main-collection-filters');
      const newFiltersForm = filtersDoc.querySelector('#CollectionFiltersForm');
      const currentFiltersForm = document.querySelector('#CollectionFiltersForm');

      if (newFilters && currentFilters) {
        currentFilters.innerHTML = newFilters.innerHTML;
      }
      if (newFiltersForm && currentFiltersForm) {
        currentFiltersForm.innerHTML = newFiltersForm.innerHTML;
        currentFiltersForm.classList.remove('loading');
      }

      // — Update banner
      if (htmlSections.bannerHTML) {
        const bannerDoc = parser.parseFromString(htmlSections.bannerHTML, 'text/html');
        const newBanner = bannerDoc.getElementById('main-collection-banner');
        const currentBanner = document.getElementById('main-collection-banner');
        if (newBanner && currentBanner) {
          currentBanner.innerHTML = newBanner.innerHTML;
        }
      }

      // — Re-bind everything on the newly inserted markup
      this.initSlideoutTriggers();
      this.bindActiveFacetButtonEvents();
    } catch (error) {
      console.error('Error updating sections:', error);
    }
  }

  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}?${searchParams}`);
  }

  reloadPage(searchParams) {
    window.location = `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`;
  }

  getSections() {
    const sections = [];
    const filters = document.getElementById('main-collection-filters');
    const grid = document.getElementById('main-collection-product-grid');
    const banner = document.getElementById('main-collection-banner');

    if (filters?.dataset.id) sections.push({ id: filters.id, section: filters.dataset.id });
    if (grid?.dataset.id) sections.push({ id: grid.id, section: grid.dataset.id });
    if (banner?.dataset.id) sections.push({ id: banner.id, section: banner.dataset.id });

    return sections;
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll('.js-facet-remove').forEach(el => {
      el.addEventListener('click', this.onActiveFilterClick, { once: true });
    });
  }
}

customElements.define('nova-filters-wrapper', NovaFiltersWrapper);
document.body.addEventListener('click', e => {
  const btn = e.target.closest('.slideout-filters__filter-button');
  const closeTargets = e.target.matches('.filter-overlay, .slideout-filters__close-button');

  if (btn && window.innerWidth < 990) {
    e.preventDefault();
    document.body.classList.toggle('filter-open');
  } else if (closeTargets) {
    document.body.classList.remove('filter-open');
  }
});